import { useCallback, useEffect, useState } from 'react';
import { approvalRoutingService, getErrorMessage } from '../../services/apiService';

/**
 * The routing rules and the people who may be named in them, loaded once for the whole
 * User Management page: the two approver tabs, their counts, the user table's "Approves"
 * column and the Add/Edit User form all read the same copy.
 */
export default function useApprovalRouting() {
  const [rules, setRules] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      const [r, a] = await Promise.all([approvalRoutingService.getRules(), approvalRoutingService.getApprovers()]);
      setRules(r?.data || []);
      setApprovers(a?.data || []);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /**
   * Put people in a combination (one rule each): VMS users by id, and people without a VMS account
   * by address. Returns what could not be added, with the server's reason, so a caller can say
   * exactly what did not happen.
   */
  const addPeople = useCallback(async (kind, nature, sub, userIds, emails = []) => {
    const failed = [];
    const base = { kind, nature_of_business: nature || null, supplier_subcategory: sub || null };
    for (const id of userIds) {
      try {
        await approvalRoutingService.createRule({ ...base, approver_user_id: Number(id) });
      } catch (e) {
        failed.push({ id, reason: getErrorMessage(e) });
      }
    }
    for (const email of emails) {
      try {
        await approvalRoutingService.createRule({ ...base, approver_email: email });
      } catch (e) {
        failed.push({ email, reason: getErrorMessage(e) });
      }
    }
    await reload();
    return failed;
  }, [reload]);

  const removeRule = useCallback(async (ruleId) => {
    await approvalRoutingService.deleteRule(ruleId);
    await reload();
  }, [reload]);

  /** Apply the Add/Edit User form's changes for one user; returns what failed. */
  const applyUserChanges = useCallback(async (userId, { add, remove }) => {
    const failed = [];
    for (const id of remove) {
      try {
        await approvalRoutingService.deleteRule(id);
      } catch (e) {
        failed.push({ reason: getErrorMessage(e) });
      }
    }
    for (const r of add) {
      try {
        await approvalRoutingService.createRule({ ...r, approver_user_id: Number(userId) });
      } catch (e) {
        failed.push({ reason: getErrorMessage(e) });
      }
    }
    if (add.length || remove.length) await reload();
    return failed;
  }, [reload]);

  return { rules, approvers, loading, error, reload, addPeople, removeRule, applyUserChanges };
}
